// src/pages/projects/hooks/useProjectDocuments.js
import { useState } from 'react';
import useProjectStore from '../../../stores/projectStore';
import useConfirmDialog from '../../../hooks/useConfirmationDialog';

export const useProjectDocuments = (projectId) => {
  // States
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadData, setUploadData] = useState({
    file: null,
    name: '',
    description: '',
    type: 'other',
    isClientVisible: false
  });
  
  // Stores
  const { 
    uploadProjectDocument, 
    deleteProjectDocument, 
    updateProjectDocument,
    isLoading, 
    error 
  } = useProjectStore();
  
  // Confirm dialog
  const [ConfirmDialogComponent, confirmDelete] = useConfirmDialog();
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadData({
      ...uploadData,
      file,
      name: file.name
    });
  };
  
  // Reset the upload form
  const resetUploadForm = () => {
    setUploadData({
      file: null,
      name: '',
      description: '',
      type: 'other',
      isClientVisible: false
    });
  };
  
  // Handle file upload
  const handleUploadDocument = async () => {
    if (!uploadData.file) return;
    
    try {
      await uploadProjectDocument(projectId, uploadData.file, {
        name: uploadData.name,
        description: uploadData.description,
        type: uploadData.type,
        isClientVisible: uploadData.isClientVisible
      });
      
      resetUploadForm();
      setIsUploadDialogOpen(false);
    } catch (err) {
      console.log('Failed to upload document:', err);
    }
  };
  
  // Handle delete document
  const handleDeleteDocument = async (document) => {
    const confirmed = await confirmDelete({
      title: 'Delete Document',
      content: `Are you sure you want to delete "${document.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteProjectDocument(projectId, document._id);
      } catch (err) {
        console.error('Failed to delete document:', err);
      }
    }
  };

  // Handle edit document
  const handleEditDocument = (document) => {
    setEditingDocument(document);
    setIsEditDialogOpen(true);
  };

  // Handle save document changes
  const handleSaveDocumentChanges = async (updateData) => {
    if (!editingDocument) return;
    
    try {
      await updateProjectDocument(projectId, editingDocument._id, updateData);
      setIsEditDialogOpen(false);
      setEditingDocument(null);
    } catch (err) {
      console.error('Failed to update document:', err);
    }
  };

  // Handle close edit dialog
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingDocument(null);
  };
  
  // Filter documents by category and search term
  const getFilteredDocuments = (documents = []) => {
    return documents.filter(doc => {
      // Apply category filter
      if (categoryFilter !== 'all' && doc.type !== categoryFilter) {
        return false;
      }
      
      // Apply search filter
      if (searchTerm && !doc.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  };
  
  return {
    // State
    isUploadDialogOpen,
    setIsUploadDialogOpen,
    isEditDialogOpen,
    setIsEditDialogOpen,
    editingDocument,
    categoryFilter,
    setCategoryFilter,
    searchTerm,
    setSearchTerm,
    uploadData,
    setUploadData,
    isLoading,
    error,
    
    // Methods
    handleFileSelect,
    handleUploadDocument,
    handleDeleteDocument,
    handleEditDocument,
    handleSaveDocumentChanges,
    handleCloseEditDialog,
    getFilteredDocuments,
    resetUploadForm,
    
    // Components
    ConfirmDialogComponent
  };
};

export default useProjectDocuments;